import React, { useMemo, useState } from 'react';
import { supabase } from '@/lib/supabaseClient';
import { toast } from 'sonner';
import { Plus, PencilSimple, Trash, Eye } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { StarText } from './TestimonialStars';
import TestimonialModal from './TestimonialModal';
import WebsitePreview from './TestimonialPreview';
import { SOURCE_STYLES, truncateReview } from './testimonialUtils';

const StatCard = ({ label, value }) => (
  <div className="bg-white border border-[#EBEBEB] rounded-[10px] px-5 py-3.5">
    <div className="text-2xl font-bold text-[#1B2D5E] font-body">{value}</div>
    <div className="text-xs text-[#888] font-body mt-0.5">{label}</div>
  </div>
);

const TestimonialsTab = ({ testimonials, onRefresh }) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [previewItem, setPreviewItem] = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  const stats = useMemo(
    () => ({
      total: testimonials.length,
      active: testimonials.filter((t) => t.is_active).length,
      featured: testimonials.filter((t) => t.is_featured).length,
    }),
    [testimonials],
  );

  const patchField = async (id, field, value) => {
    try {
      const { error } = await supabase.from('testimonials').update({ [field]: value }).eq('id', id);
      if (error) throw error;
      onRefresh();
    } catch {
      toast.error('Failed to update testimonial');
    }
  };

  const handleDelete = async (t) => {
    if (!window.confirm(`Delete review from ${t.customer_name}?`)) return;
    try {
      const { error } = await supabase.from('testimonials').delete().eq('id', t.id);
      if (error) throw error;
      toast.success('Testimonial deleted');
      onRefresh();
    } catch {
      toast.error('Failed to delete testimonial');
    }
  };

  const openAdd = () => {
    setEditing(null);
    setModalOpen(true);
  };

  const openEdit = (t) => {
    setEditing(t);
    setModalOpen(true);
  };

  return (
    <div>
      <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
        <div>
          <h2 className="font-display text-xl text-[#1B2D5E]">Manage Testimonials</h2>
          <p className="text-sm text-[#888] font-body mt-1">Customer reviews shown on website</p>
        </div>
        <Button
          onClick={openAdd}
          className="bg-[#1B2D5E] hover:bg-[#E8651A] text-white rounded-lg px-5 py-2.5 font-body"
        >
          <Plus size={18} className="mr-2" />
          Add Testimonial
        </Button>
      </div>

      <div className="grid sm:grid-cols-3 gap-4 mb-6">
        <StatCard label="Total Reviews" value={stats.total} />
        <StatCard label="Active" value={stats.active} />
        <StatCard label="Featured" value={stats.featured} />
      </div>

      <div className="bg-white border border-[#EBEBEB] rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-10">#</TableHead>
                <TableHead>Customer</TableHead>
                <TableHead>Route</TableHead>
                <TableHead>Rating</TableHead>
                <TableHead className="min-w-[180px]">Review</TableHead>
                <TableHead>Source</TableHead>
                <TableHead>Featured</TableHead>
                <TableHead>Active</TableHead>
                <TableHead className="w-20">Order</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {testimonials.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} className="text-center py-12 text-[#888]">
                    No testimonials yet. Click &quot;Add Testimonial&quot; to get started.
                  </TableCell>
                </TableRow>
              ) : (
                testimonials.map((t, idx) => {
                  const sourceClass = SOURCE_STYLES[t.source] || SOURCE_STYLES.Other;
                  const isExpanded = expandedId === t.id;
                  return (
                    <TableRow key={t.id} className="font-body text-[13px] hover:bg-[#FAFAFA]">
                      <TableCell className="text-[#888]">{idx + 1}</TableCell>
                      <TableCell className="font-medium">{t.customer_name}</TableCell>
                      <TableCell className="max-w-[140px] truncate" title={t.route}>
                        {t.route || '—'}
                      </TableCell>
                      <TableCell>
                        <StarText rating={t.rating} />
                      </TableCell>
                      <TableCell>
                        <button
                          type="button"
                          className="text-left text-[#555] hover:text-[#1B2D5E]"
                          title={t.review_text}
                          onClick={() => setExpandedId(isExpanded ? null : t.id)}
                        >
                          {isExpanded ? t.review_text : truncateReview(t.review_text)}
                        </button>
                      </TableCell>
                      <TableCell>
                        <span className={`text-[11px] font-medium px-2.5 py-0.5 rounded-full ${sourceClass}`}>
                          {t.source}
                        </span>
                      </TableCell>
                      <TableCell>
                        <Switch
                          checked={t.is_featured}
                          onCheckedChange={(v) => patchField(t.id, 'is_featured', v)}
                        />
                      </TableCell>
                      <TableCell>
                        <Switch
                          checked={t.is_active}
                          onCheckedChange={(v) => patchField(t.id, 'is_active', v)}
                        />
                      </TableCell>
                      <TableCell>
                        <Input
                          type="number"
                          min="0"
                          className="h-8 w-16 text-xs"
                          defaultValue={t.display_order}
                          onBlur={(e) => {
                            const val = parseInt(e.target.value, 10);
                            if (!Number.isNaN(val) && val !== t.display_order) {
                              patchField(t.id, 'display_order', val);
                            }
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Button size="sm" variant="ghost" onClick={() => openEdit(t)} title="Edit">
                            <PencilSimple size={16} />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => setPreviewItem(t)} title="Preview">
                            <Eye size={16} />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-red-600 hover:text-red-700"
                            onClick={() => handleDelete(t)}
                            title="Delete"
                          >
                            <Trash size={16} />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      <TestimonialModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        editing={editing}
        onSaved={onRefresh}
      />

      <Dialog open={!!previewItem} onOpenChange={(open) => !open && setPreviewItem(null)}>
        <DialogContent className="font-body max-w-[640px]">
          <DialogHeader>
            <DialogTitle className="font-display text-[#1B2D5E]">Website Preview</DialogTitle>
          </DialogHeader>
          {previewItem && <WebsitePreview testimonial={previewItem} />}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TestimonialsTab;
